import { useAuthentication } from '@ecosystem/newron-client-lib';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PreferenceMutateOptions,
  PreferenceRequest,
  PreferenceResponse,
  PreferenceResponseStatus,
} from '@utils/userPreferencesUtils/Types';
import { PreferenceApiService } from '@services/userPreferences/PreferencesApi.service';

export interface CreatePreferenceMutateResponse<T> {
  data: PreferenceResponse<T> | null;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  mutate: (variables: PreferenceRequest<T>, options?: PreferenceMutateOptions<T>) => void;
  mutateAsync: (
    variables: PreferenceRequest<T>,
    options?: PreferenceMutateOptions<T>,
  ) => Promise<PreferenceResponse<T>>;
  reset: () => void;
}

/**
 * @return CreatePreferenceMutateResponse Object
 *
 * @remarks
 * Hooks for create preference api call using useMutate to manage server state
 *
 * @param type       - to difference the different pages (example: "category-leaderboard-group" for Category Leaderboard Page)
 * @param namespace  - combination of retailer client and country
 * **/
export const useCreatePreferences = <T extends Record<string, any>>(
  type: string,
  namespace: string,
): CreatePreferenceMutateResponse<T> => {
  const { getAccessToken } = useAuthentication();

  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, isError, mutate, mutateAsync, reset } = useMutation({
    mutationKey: ['preferences', type, namespace],

    mutationFn: async (request: PreferenceRequest<T>) => {
      const userToken = await getAccessToken();

      return PreferenceApiService.createPreferences(
        userToken ?? '',
        { type, namespace, key: request.key },
        request.data,
      );
    },
    onSuccess: (response) => {
      if (response.status === PreferenceResponseStatus.SUCCESS) {
        queryClient.invalidateQueries({ queryKey: ['preferences'] });
      }
    },
  });

  return {
    data: data ?? null,
    isLoading: isPending,
    isSuccess,
    isError,
    mutate,
    mutateAsync,
    reset,
  };
};
